import { Header } from '@/ui/core/header'
import { VStack } from '@/ui/core/stack'
import { format, isSameDay, parseISO } from 'date-fns'
import { View } from 'react-native'

/**
 * A visit's date as the brand's display numeral: the day large, the month and weekday small
 * and uppercase beneath it. The one thing a member wants to know about a visit at a glance,
 * set the way the website sets its numbers.
 *
 * `startTimeLocal` is the studio's wall clock, and is formatted as written, never converted.
 */
export const VisitDate = ({
	startTimeLocal,
	size = 'sm',
	tone = 'ink',
}: {
	startTimeLocal: string
	size?: 'sm' | 'lg'
	tone?: 'ink' | 'white'
}) => {
	const date = parseISO(startTimeLocal)

	return (
		<VStack gap={2} className="items-start">
			<Header variant={size === 'lg' ? 'display' : 'display-sm'} tone={tone}>
				{format(date, 'd')}
			</Header>
			<View className="flex-row gap-2">
				<Header variant="h7" tone={tone}>
					{format(date, 'MMM')}
				</Header>
				<Header variant="h7" tone={tone === 'white' ? 'white' : 'muted'}>
					{format(date, 'EEE')}
				</Header>
			</View>
		</VStack>
	)
}

/** "9:30 AM", from the same wall clock. */
export const visitTime = (startTimeLocal: string) =>
	format(parseISO(startTimeLocal), 'h:mm a')

/**
 * "Tue, Sep 29" until the day itself, then "Today".
 *
 * A date is what a member checks a week out. On the morning of the visit it is a fact they can
 * read off the phone's own clock, and the word is what tells them the screen knows which day it
 * is. Compared on the studio's wall clock, because that is the day they mean.
 */
export const visitDayLine = (startTimeLocal: string, now: Date) => {
	const date = parseISO(startTimeLocal)

	return isSameDay(date, now) ? 'Today' : format(date, 'EEE, MMM d')
}
